import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import uuidv4 from 'uuid/v4';

import cn from 'classnames';

import { get, isEqual, map } from 'lodash-es';
import { getLabel, getNewTitle } from '../../Utils/utils';

import ButtonAction from '../Common/Blocks/ButtonAction';
import { ActionButtonEnum } from '../Common/Blocks/ButtonAction/ButtonAction.enum';

import QuestionnaireBlock from '../QuestionnaireBlock/QuestionnaireBlock';
import { questionnaireShape } from '../QuestionnaireBlock/QuestionnaireBlock.shape';
import ErrorCustom from '../QuestionnaireBlock/Fields/ErrorCustom';

import './GraduatedLoanBlock.scss';

const GraduatedLoanBlock = ({
  graduatedLoans,
  eventEmitter,
  strings,
  willResetForm,
  onResetForm,
  loanId,
  validateFormGraduatedLoans,
  willValidate,
  i18n,
}) => {
  const [stateBlock, setStateBlock] = useState(graduatedLoans);
  const [freezeStateBlock, setFreezeStateBlock] = useState(graduatedLoans);
  const [errorsState, setErrorsState] = useState({});

  const onResetFormLocal = () => {
    setStateBlock(graduatedLoans);
    setFreezeStateBlock(graduatedLoans);

    onResetForm(graduatedLoans);
  };

  const getNewEmptyBlock = (block, newIndex) => ({
    ...block,
    id: `${uuidv4()}`,
    sequence: newIndex,
    parts: map(block.parts, part => ({
      ...part,
      questions: map(part.questions, q => ({ ...q, value: undefined })),
    })),
  });

  const onClickAdd = () => {
    setStateBlock([
      ...stateBlock,
      getNewEmptyBlock(graduatedLoans[0], freezeStateBlock.length + 1, stateBlock.length + 1),
    ]);
  };

  const onClickRemove = id => () => {
    const newStateBlock = stateBlock.filter(block => block.id !== id);

    setStateBlock(newStateBlock);

    eventEmitterLocal({ id: `${graduatedId}${id}`, isValid: null, answers: null });
  };

  useEffect(() => {
    // Use freezeStateBlock to freeze initial state, so we can add/remove loans to stateBlock
    // If graduatedLoans is updated (save or reset), so different from freezeStateBlock, it will be updated
    if (!isEqual(graduatedLoans, freezeStateBlock)) {
      setStateBlock(graduatedLoans);
      setFreezeStateBlock(graduatedLoans);
    }
  }, [graduatedLoans]);

  // Validate if it comes from Questionnaire above (if changing Loan Duration, we need to revalidate graduateds)
  useEffect(() => {
    if (willValidate) {
      eventEmitterLocal({});
    }
  }, [willValidate]);

  // Validate for each change event
  const eventEmitterLocal = values => {
    if (validateFormGraduatedLoans) {
      const errors = validateFormGraduatedLoans(loanId, values);

      setErrorsState(errors);
    }

    eventEmitter(values);
  };

  const getCodeFirstError = errors => {
    for (const e in errors) {
      return errors[e];
    }
  };

  const graduatedId = `graduated_${loanId}_`;

  const head = (block, idx) => (
    <div className="head">
      <span className="title" role="presentation">
        {getNewTitle({
          title: getLabel({
            label: block?.title,
            strings,
            defaultCode: `editsalesroute.graduated.title`,
            defaultString: 'Palier',
          }),
          isTitleMultiple: block?.isTitleMultiple,
          idx: idx + 1,
        })}
      </span>
    </div>
  );

  return (
    <div className="graduated-loan-block">
      {map(stateBlock, (block, idx) => (
        <div className="graduated-line" key={block.id}>
          {head(block, idx)}
          <QuestionnaireBlock
            questionnaire={block}
            id={`${graduatedId}${block.id}`}
            eventEmitter={eventEmitterLocal}
            stringsProp={strings}
            willResetForm={willResetForm}
            onResetForm={onResetFormLocal}
            className="graduated-fields-a74b0293-5f0e-491a-ae84-8ab57e3e8e6e"
            i18n={i18n}
            strings={strings}
          />
          <ButtonAction
            // disabled={block.disabled}
            typeAction={ActionButtonEnum.REMOVE}
            action={onClickRemove(block.id)}
            label={get(strings, 'editsalesroute.loan.graduated.button.remove', 'Supprimer')}
            className={cn({ show: idx, hide: !idx })}
          />
        </div>
      ))}
      <ErrorCustom error={getCodeFirstError(errorsState)} />
      <ButtonAction
        typeAction={ActionButtonEnum.ADD}
        action={onClickAdd}
        label={get(strings, 'editsalesroute.loan.graduated.button.add', 'Ajouter un palier')}
      />
    </div>
  );
};

GraduatedLoanBlock.propTypes = {
  graduatedLoans: PropTypes.arrayOf(questionnaireShape).isRequired,
  eventEmitter: PropTypes.func.isRequired,
  strings: PropTypes.object.isRequired,
  willResetForm: PropTypes.bool,
  onResetForm: PropTypes.func,
  loanId: PropTypes.string.isRequired,
  validateFormGraduatedLoans: PropTypes.func,
  willValidate: PropTypes.bool,
  i18n: PropTypes.object,
};

GraduatedLoanBlock.defaultProps = {
  willResetForm: false,
  onResetForm: () => {},
  validateFormGraduatedLoans: undefined,
  willValidate: false,
  i18n: {},
};

export default GraduatedLoanBlock;
